import { BehaviorSubject, Observable } from "rxjs";
import { AjaxUtil } from "../utils/ajax-util";

/**
 * This is a Vanilla Javascript solution that communicates with React and other frameworks using
 * Javascript Events. The key event is "Session"
 */
export type SessionStatusTypes = 'logged-in' | 'logged-out';
export type HarnessStatusTypes = 'active' | 'inactive' | 'unavailable';
export type sessionType = {
	loggedInStatus$: Observable<SessionStatusTypes>;
	harnessStatus$: Observable<HarnessStatusTypes>;
	begin: () => void;
	renew: () => void;
	end: () => void;
	harnessEnabled: () => void,
	harnessConfigOpened: () => void,
	harnessConfigClosed: () => void,
	postRequest: (path: string, data?: Object) => Promise<any>;
	getRequest: (path: string) => Promise<any>;
};

type HttpMethodTypes = 'get'|'post';

export type DayforceWindowType = {
	AppSettingsData?: {
		applicationPath?: string,
		clientBrowserVal?: string,
		clientBrowserId?: string,
		csrfRequestToken?: string,
	},
	LabelData?: {
		culture?: string,
		operatingCountry?: string,
		showKeys?: string,
	},
};

/**
 * This is a singleton (Module pattern) used to persist Session data on the front-end
 */
export const session: sessionType = (function() {
	let heartbeatId;
	const heartbeatTimeout = 180000; // 60000 = 1 minute in milliseconds.
	const sessionStatus$ = new BehaviorSubject<SessionStatusTypes>("logged-out");
	const loggedInStatus$: Observable<SessionStatusTypes> = sessionStatus$.asObservable();
	const harnessStatusSubject$ = new BehaviorSubject<HarnessStatusTypes>("unavailable");
	const harnessStatus$: Observable<HarnessStatusTypes> = harnessStatusSubject$.asObservable();
	/**
	 * Check for a Dayforce Object on the Window and use this to start the session.
	 * The back-end sets the clientBrowserId cookie with httpsecure so the front end can't see it
	 * verifyLogin will fail if the cookie is not preset, so this verifies both the cookie and the session.
	 *
	 */
	const bootSession = (): void => {
		if (window["Dayforce"]) {
			const dayforce: DayforceWindowType = window["Dayforce"];
			if (dayforce.AppSettingsData?.clientBrowserId) {
				verifyLogin();
			}
		}
	}

	/**
	 * used to begin the session only if the backend verifies the session is valid.
	 */
	const verifyLogin = (): void => {
		// If heartbeat is successful - it will call renew starting the session after verifying.
		heartbeatId = setTimeout(heartbeat, 0);
	}

	/**
	 * The following methods are not exposed on the session object.
	 */
	async function heartbeat() {
		const path = AjaxUtil.addClientBrowserId('/Framework/Timeout/SendHeartbeat');
		try {
			await postRequest(path, {});
		} catch(e) {
			// heartbeat failed - cancel session.
			console.log('heartbeat threw an error: ', e);
			session.end();
		}
	}

	/**
	 * used After validating an API request.
	 */
	const begin = (): void => {
		if (sessionStatus$.getValue() === 'logged-in') {
			return;
		}
		heartbeatId = setInterval(() => heartbeat(), heartbeatTimeout);
		sessionStatus$.next('logged-in');
	};

	const renew = (): void => {
		clearInterval(heartbeatId);
		sessionStatus$.next('logged-in');
		heartbeatId = setInterval(heartbeat, heartbeatTimeout);
	}

	const end = (): void => {
		if (heartbeatId) {
			clearInterval(heartbeatId);
		}
		sessionStatus$.next('logged-out');
	};

	const harnessEnabled = (): void => {
		harnessStatusSubject$.next('inactive');
	}

	const harnessConfigOpened = (): void => {
		if (harnessStatusSubject$.getValue() === 'unavailable') {
			return;
		}
		harnessStatusSubject$.next('active');
	}

	const harnessConfigClosed = (): void => {
		if (harnessStatusSubject$.getValue() === 'unavailable') {
			return;
		}
		harnessStatusSubject$.next('inactive');
	}

	/**
	 * Priviledged API requests that should be handled by a unifies response handler
	 * @param path string
	 * @param data
	 */
	const postRequest = async (path: string, data: Object = {}): Promise<any> => {
		return await apiRequest({path, httpMethod: 'post', data})
	}

	const getRequest = async (path: string): Promise<any> => {
		return await apiRequest({path, httpMethod: 'get'})
	}

	const apiRequest = async ({path, httpMethod, data}: {path: string, httpMethod: HttpMethodTypes, data?: any}): Promise<any> => {
		try {
			const res = (httpMethod === 'post')
				? await AjaxUtil.post(path, data, true)
				: await AjaxUtil.get(path, true);
			return await handleResponse(res);
		} catch(e) {
			console.log('ajax_utils.apiRequest failed ', {e});
			session.end();
			throw e;
		}
	}

	async function handleResponse(res: any) {
		// some Ceridian servers do not include response text like "OK", so only check the numerical status code
		if (res.status === 200 || res.status === 201) {
			// test for a redirect to MyDayforce.aspx - horrible result of a BAD Api request
			if (/mydayforce\.aspx/i.test(res.request.responseURL)) {
				// request is redirected to the HOME page, user must log in again!
				session.end();
				return false;
			}
			session.renew();
			return res.data;
		}
		// an error occurred
		console.log('unknown FAILURE ajax_utils.handleResponse', res);
		throw new Error('Request failed');
	}
	bootSession();

	/**
	 * The return method here exposes the named methods for this singleton instance.
	 */
	return {
		loggedInStatus$,
		harnessStatus$,
		begin,
		renew,
		end,
		harnessEnabled,
		harnessConfigOpened,
		harnessConfigClosed,
		postRequest,
		getRequest,
	};
})();
